import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks';

// Eagerly loaded (critical path)
import Home from './pages/Home.jsx';
import Layout from './components/Layout.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { ToastProvider } from './components/Toast.jsx';
import AgeGate from './components/AgeGate.jsx';

// Lazy loaded pages (code splitting for performance)
const Debate = lazy(() => import('./pages/Debate.jsx'));
const Profile = lazy(() => import('./pages/Profile.jsx'));
const WorldMap = lazy(() => import('./pages/WorldMap.jsx'));
const Matchmaking = lazy(() => import('./pages/Matchmaking.jsx'));
const Arena = lazy(() => import('./pages/Arena.jsx'));
const ArenaSetup = lazy(() => import('./pages/ArenaSetup.jsx'));
const Arena10v1 = lazy(() => import('./pages/Arena10v1.jsx'));
const Arena10v1Lobby = lazy(() => import('./pages/Arena10v1Lobby.jsx'));
const Communities = lazy(() => import('./pages/Communities.jsx'));
const CommunityHome = lazy(() => import('./pages/CommunityHome.jsx'));
const Analytics = lazy(() => import('./pages/Analytics.jsx'));
const Pantheon = lazy(() => import('./pages/Pantheon.jsx'));
const Tournament = lazy(() => import('./pages/Tournament.jsx'));
const Store = lazy(() => import('./pages/Store.jsx'));
const Contact = lazy(() => import('./pages/Contact.jsx'));
const LandingPage = lazy(() => import('./pages/LandingPage.jsx'));

// Loading fallback for lazy components
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AgeGate>
        <ToastProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
          {/* 1. PUBLIC ROOT: Defaults to Layout (Login) if not logged in */}
          {/* Landing Page removed from root as requested. Accessible via manual link only. */}
          <Route path="/landingpage" element={<LandingPage />} />

          {/* 1b. AUTH ROUTE: Explicit route for deep linking or redirects */}
          <Route path="/auth" element={currentUser ? <Navigate to="/" replace /> : <Layout />} />

          {/* 2. APP LAYOUT: Handles Sidebar, Auth Wall, and Main App Routes */}
          <Route element={<Layout />}>
            {/* If logged in, Layout renders Outlet -> Home.
                If not logged in, Layout renders Auth UI (Login Page). */}
            <Route path="/" element={<Home />} />

            <Route path="/map" element={<WorldMap />} />
            <Route path="/matchmaking" element={<Matchmaking />} />
            <Route path="/communities" element={<Communities />} />
            <Route path="/community/:communityId" element={<CommunityHome />} />
            <Route path="/debate/:topicId" element={<Debate />} />
            <Route path="/arena/:debateId" element={<Arena />} />
            <Route path="/arena/setup" element={<ArenaSetup />} />
            <Route path="/arena10v1-lobby" element={<Arena10v1Lobby />} />
            <Route path="/arena10v1/:id" element={<Arena10v1 />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/pantheon" element={<Pantheon />} />
            <Route path="/tournament" element={<Tournament />} />
            <Route path="/store" element={<Store />} />
            <Route path="/contact" element={<Contact />} />
          </Route>

            {/* 3. FALLBACK */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </AgeGate>
    </ErrorBoundary>
  );
}

export default App;
